/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Stack, Paper } from '@mui/material'
import { Button, CircularProgress, Grid, Typography, IconButton, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CropLandscape, CropPortrait } from '@material-ui/icons'
import ClearIcon from '@mui/icons-material/Clear'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddIcon from '@mui/icons-material/Add'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { styled } from '@mui/material/styles'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  DragOverlay,
  pointerWithin
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Formik, Form } from 'formik'
import moment from 'moment'
import { HexColorPicker } from 'react-colorful'

import { addBadgeConfigurationSchema } from 'schemas/badges'
import CustomTextField from 'components/Form/CustomTextField'
import badgesSlice, {
  addBadgeConfiguration,
  updateBadgeConfiguration,
  getBadgeConfiguration
} from 'store/badges'
import {
  badgesSubjectsApplication,
  badgeConfigurationDefaultFields,
  possibleBadgeConfigurationFields
} from 'constants/index'
import Wrapper from 'components/Wrapper'
import CustomTabs from 'components/CustomTabs'
import ConfirmModal from 'components/ConfirmationModal'
import BadgePreview from './BadgePreview'

export const FormErrorMessage = styled(Typography)({
  color: '#FF0000',
  fontSize: '12px',
  marginLeft: '10px'
})

const PRESET_COLORS = ['#E53935', '#8E24AA', '#00ACC1', '#43A047', '#FB8C00', '#212121']

const BADGE_LINE_COUNT = 5

const DraggableAvailableChip = ({ fieldKey, label, classes }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `available::${fieldKey}`,
    data: { type: 'available', fieldKey }
  })
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 100 : 'auto',
    position: 'relative'
  }
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={classes.availableFieldChip}>
      <DragIndicatorIcon style={{ fontSize: 13, color: '#bbb', marginRight: 3 }} />
      <Typography className={classes.availableFieldName}>{label}</Typography>
    </Box>
  )
}

const SortableFieldChip = ({ id, label, onRemove, classes }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 100 : 'auto'
  }
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={classes.lineFieldChip}>
      <DragIndicatorIcon style={{ fontSize: 14, color: '#bbb', marginRight: 2 }} />
      <Typography className={classes.lineFieldName}>{label}</Typography>
      <Box
        className={classes.removeBtn}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onRemove}>
        ×
      </Box>
    </Box>
  )
}

const LineBlock = ({
  lineIndex,
  label,
  isLocked,
  values,
  setFieldValue,
  classes,
  assignableLines,
  handleRemoveFromLine,
  isOver
}) => {
  const { setNodeRef } = useDroppable({
    id: `line-drop::${lineIndex}`,
    data: { type: 'line', lineIndex }
  })

  const lineFields = isLocked ? null : assignableLines[lineIndex] || []
  const lineFontSize = (values.badgeLineFontSizes || [22, 12, 12, 12, 12])[lineIndex] ?? 12
  // Line 1 (the name) can run a little larger than the body lines below it — those
  // share tight vertical space with the QR code and footer bar in the fixed-height card.
  const maxFontSize = lineIndex === 0 ? 28 : 15

  return (
    <Box
      ref={isLocked ? undefined : setNodeRef}
      className={classes.lineBlock}
      style={
        !isLocked && isOver ? { borderColor: '#00BCD4', backgroundColor: '#f0fbfd' } : undefined
      }>
      <Box className={classes.lineLabelRow}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography className={classes.lineLabel}>{label}</Typography>
        </Box>
        <Box className={classes.fontSizeControl}>
          <Typography className={classes.fontSizeHeading}>Font size</Typography>
          <Box className={classes.fontSizeInputWrapper}>
            <Box
              className={`${classes.fontSizeBtn} ${
                lineFontSize <= 1 ? classes.fontSizeBtnDisabled : ''
              }`}
              onClick={() => {
                const val = lineFontSize - 1
                if (val < 1) return
                const sizes = [...(values.badgeLineFontSizes || [22, 12, 12, 12, 12])]
                sizes[lineIndex] = val
                setFieldValue('badgeLineFontSizes', sizes)
              }}>
              −
            </Box>
            <input
              type="number"
              min={1}
              max={maxFontSize}
              step={1}
              value={lineFontSize}
              onChange={(e) => {
                const val = parseFloat(e.target.value)
                if (isNaN(val)) return
                const sizes = [...(values.badgeLineFontSizes || [22, 12, 12, 12, 12])]
                sizes[lineIndex] = Math.min(maxFontSize, Math.max(1, val))
                setFieldValue('badgeLineFontSizes', sizes)
              }}
              className={classes.fontSizeInput}
            />
            <Box
              className={`${classes.fontSizeBtn} ${
                lineFontSize >= maxFontSize ? classes.fontSizeBtnDisabled : ''
              }`}
              onClick={() => {
                const val = lineFontSize + 1
                if (val > maxFontSize) return
                const sizes = [...(values.badgeLineFontSizes || [22, 12, 12, 12, 12])]
                sizes[lineIndex] = val
                setFieldValue('badgeLineFontSizes', sizes)
              }}>
              +
            </Box>
          </Box>
        </Box>
      </Box>
      {isLocked && (
        <Typography className={classes.lockedText}>
          First Name and Last Name are always printed on this line — select your preferred format
          below
        </Typography>
      )}
      <Box className={classes.lineDropZone}>
        {isLocked ? (
          <Box style={{ display: 'flex', gap: 16, marginTop: 4 }}>
            {[
              { fmt: 'firstName lastName', label: 'First Last', example: 'e.g. John Doe' },
              { fmt: 'lastName, firstName', label: 'Last, First', example: 'e.g. Doe, John' }
            ].map(({ fmt, label, example }) => {
              const isActive = (values.badgeNameFormat || 'firstName lastName') === fmt
              return (
                <Box key={fmt} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Box
                    className={`${classes.nameFormatCard} ${
                      isActive ? classes.nameFormatCardActive : ''
                    }`}
                    onClick={() => setFieldValue('badgeNameFormat', fmt)}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Box
                        className={`${classes.nameFormatRadio} ${
                          isActive ? classes.nameFormatRadioActive : ''
                        }`}
                      />
                      <Typography className={classes.nameFormatCardLabel}>{label}</Typography>
                    </Box>
                  </Box>
                  <Typography className={classes.nameFormatCardExample}>{example}</Typography>
                </Box>
              )
            })}
          </Box>
        ) : lineFields.length === 0 ? (
          <Typography className={classes.emptyLine}>
            {isOver ? 'Drop here' : 'Drag fields here'}
          </Typography>
        ) : (
          <SortableContext items={lineFields} strategy={horizontalListSortingStrategy}>
            {lineFields.map((key) => {
              const field = possibleBadgeConfigurationFields.find((f) => f.key === key)
              return (
                <SortableFieldChip
                  key={key}
                  id={key}
                  label={field?.value || key}
                  onRemove={() => handleRemoveFromLine(lineIndex, key)}
                  classes={classes}
                />
              )
            })}
          </SortableContext>
        )}
      </Box>
    </Box>
  )
}

const BadgeLayoutSection = ({ values, setFieldValue }) => {
  const classes = useLayoutStyles()
  const [activeDragId, setActiveDragId] = useState(null)
  const [overLineIndex, setOverLineIndex] = useState(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const assignableFields = possibleBadgeConfigurationFields.filter(
    (f) => f.key !== 'firstName' && f.key !== 'lastName'
  )

  const placedKeys = (values.badgeLayout || []).flat()
  const availableFields = assignableFields.filter((f) => !placedKeys.includes(f.key))

  const handleRemoveFromLine = (lineIndex, fieldKey) => {
    const layout = [...(values.badgeLayout || Array.from({ length: BADGE_LINE_COUNT }, () => []))]
    const updated = layout.map((line, i) =>
      i === lineIndex ? line.filter((k) => k !== fieldKey) : line
    )
    setFieldValue('badgeLayout', updated)
  }

  const handleUnifiedDragStart = ({ active }) => {
    setActiveDragId(active.id)
  }

  const handleUnifiedDragOver = ({ over }) => {
    if (!over) {
      setOverLineIndex(null)
      return
    }
    if (over.id.toString().startsWith('line-drop::')) {
      setOverLineIndex(parseInt(over.id.toString().replace('line-drop::', ''), 10))
    } else {
      // hovering over a sortable chip — find which line it belongs to
      const layout = values.badgeLayout || []
      const lineIdx = layout.findIndex((line) => line.includes(over.id.toString()))
      setOverLineIndex(lineIdx >= 0 ? lineIdx : null)
    }
  }

  const handleUnifiedDragEnd = ({ active, over }) => {
    const snapshotOverLineIndex = overLineIndex
    setActiveDragId(null)
    setOverLineIndex(null)
    if (!over && snapshotOverLineIndex === null) return

    const layout = [...(values.badgeLayout || Array.from({ length: BADGE_LINE_COUNT }, () => []))]
    const activeId = active.id.toString()
    const overId = over ? over.id.toString() : null

    // Resolve target line: prefer snapshotOverLineIndex (tracked via onDragOver) over parsing overId
    const resolveTargetLine = () => {
      if (snapshotOverLineIndex !== null) return snapshotOverLineIndex
      if (!overId) return -1
      if (overId.startsWith('line-drop::')) return parseInt(overId.replace('line-drop::', ''), 10)
      return layout.findIndex((line) => line.includes(overId))
    }

    // Dragging from available fields onto a line
    if (activeId.startsWith('available::')) {
      const fieldKey = activeId.replace('available::', '')
      const targetLineIndex = resolveTargetLine()
      if (targetLineIndex === null || targetLineIndex < 1) return
      const updated = layout.map((line, i) => (i === targetLineIndex ? [...line, fieldKey] : line))
      setFieldValue('badgeLayout', updated)
      return
    }

    // Dragging a placed chip
    const sourceLineIndex = layout.findIndex((line) => line.includes(activeId))
    if (sourceLineIndex === -1) return

    const targetLineIndex = resolveTargetLine()
    if (targetLineIndex === -1 || targetLineIndex === 0) return

    if (sourceLineIndex === targetLineIndex) {
      // Same line — reorder only if dropped on another chip
      if (!overId || activeId === overId) return
      const line = layout[sourceLineIndex]
      const oldIndex = line.indexOf(activeId)
      const newIndex = line.includes(overId) ? line.indexOf(overId) : -1
      if (oldIndex === -1 || newIndex === -1) return
      const reordered = arrayMove(line, oldIndex, newIndex)
      const updated = layout.map((l, i) => (i === sourceLineIndex ? reordered : l))
      setFieldValue('badgeLayout', updated)
    } else {
      // Different line — move chip across
      const updated = layout.map((line, i) => {
        if (i === sourceLineIndex) return line.filter((k) => k !== activeId)
        if (i === targetLineIndex) return [...line, activeId]
        return line
      })
      setFieldValue('badgeLayout', updated)
    }
  }

  const lineLabels = ['LINE 1', 'LINE 2', 'LINE 3', 'LINE 4', 'LINE 5']
  const assignableLines = values.badgeLayout || Array.from({ length: BADGE_LINE_COUNT }, () => [])

  // figure out label for drag overlay
  const activeDragLabel = (() => {
    if (!activeDragId) return null
    if (activeDragId.startsWith('available::')) {
      const key = activeDragId.replace('available::', '')
      return possibleBadgeConfigurationFields.find((f) => f.key === key)?.value || key
    }
    return (
      possibleBadgeConfigurationFields.find((f) => f.key === activeDragId)?.value || activeDragId
    )
  })()

  const lineBlockProps = (lineIndex) => ({
    lineIndex,
    label: lineLabels[lineIndex],
    isLocked: lineIndex === 0,
    values,
    setFieldValue,
    classes,
    assignableLines,
    handleRemoveFromLine,
    isOver: overLineIndex === lineIndex
  })

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleUnifiedDragStart}
      onDragOver={handleUnifiedDragOver}
      onDragEnd={handleUnifiedDragEnd}>
      <Box>
        {/* Available fields bar */}
        <Box className={classes.availableBar}>
          <Box className={classes.availableTitleRow} style={{ marginBottom: 10 }}>
            <Typography className={classes.availableTitle}>BADGE FIELDS</Typography>
            <Typography className={classes.availableGuideText}>
              — Drag any field onto a line below to include it on the badge
            </Typography>
          </Box>
          <Box className={classes.availableFieldsRow}>
            {availableFields.length === 0 ? (
              <Typography className={classes.emptyAvailable}>
                No fields left — all are assigned to lines below
              </Typography>
            ) : (
              availableFields.map((f) => (
                <DraggableAvailableChip
                  key={f.key}
                  fieldKey={f.key}
                  label={f.value}
                  classes={classes}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Lines grid: 1+2, 3+4, 5 */}
        <Box className={classes.linesGrid}>
          <Box className={classes.linesGridRow}>
            <LineBlock {...lineBlockProps(0)} />
            <LineBlock {...lineBlockProps(1)} />
          </Box>
          <Box className={classes.linesGridRow}>
            <LineBlock {...lineBlockProps(2)} />
            <LineBlock {...lineBlockProps(3)} />
          </Box>
          <Box className={classes.linesGridRow} style={{ width: 'calc(50% - 4px)' }}>
            <LineBlock {...lineBlockProps(4)} />
          </Box>
        </Box>
      </Box>

      <DragOverlay>
        {activeDragLabel ? (
          <Box
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '5px 10px',
              border: '1px solid #00BCD4',
              borderRadius: '6px',
              backgroundColor: '#f0fbfd',
              gap: '4px',
              boxShadow: '0 4px 12px rgba(0,188,212,0.25)',
              cursor: 'grabbing'
            }}>
            <DragIndicatorIcon style={{ fontSize: 14, color: '#bbb', marginRight: 2 }} />
            <Typography style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
              {activeDragLabel}
            </Typography>
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

const ConfigurationForm = (props) => {
  const { title, configurationId, onClose, hideHeader, submitRef, onSavingChange, pageTitle } =
    props
  const classes = useStyles()
  const dispatch = useDispatch()
  const { conference } = useSelector((state) => state.conference)
  const { badgeConfiguration } = useSelector((state) => state.badges)

  const [addingConfiguration, setAddingConfiguration] = useState(false)
  const [loadingConfiguration, setLoadingConfiguration] = useState(false)
  const [initialOrientation, setInitialOrientation] = useState(
    () => localStorage.getItem('badgeOrientation') || 'landscape'
  )
  const [previewAs, setPreviewAs] = useState(badgesSubjectsApplication[0])
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const colorPickerRef = useRef(null)
  const dirtyRef = useRef(false)

  const requestClose = () => {
    if (dirtyRef.current && !addingConfiguration) {
      setDiscardConfirmOpen(true)
    } else {
      onClose()
    }
  }

  useEffect(() => {
    if (!colorPickerOpen) return
    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setColorPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [colorPickerOpen])

  useEffect(() => {
    const getData = async () => {
      setLoadingConfiguration(true)
      await dispatch(getBadgeConfiguration(configurationId, conference?.id))
      setLoadingConfiguration(false)
    }
    if (configurationId && conference?.id) {
      getData()
    }
    return () => {
      dispatch(badgesSlice.actions.setBadgeConfiguration(null))
    }
  }, [configurationId, conference?.id, dispatch])

  useEffect(() => {
    if (!badgeConfiguration) return
    const savedOrientation = localStorage.getItem('badgeOrientation')
    if (!savedOrientation && badgeConfiguration.badgeOrientation) {
      setInitialOrientation(badgeConfiguration.badgeOrientation)
    }
  }, [badgeConfiguration])

  const initialValues = {
    title: badgeConfiguration?.title ?? '',
    badgeBarColor: badgeConfiguration?.badgeBarColor ?? '#E53935',
    fieldsAppliedInsideQRCode: Array.from(
      new Set([
        'firstName',
        'lastName',
        ...(badgeConfiguration?.fieldsAppliedInsideQRCode ??
          badgeConfigurationDefaultFields.map((f) => f.key))
      ])
    ),
    applicableSubjects: badgeConfiguration?.applicableSubjects ?? [],
    badgeLayout: badgeConfiguration?.badgeLayout ?? [
      ['firstName', 'lastName'],
      ['credential'],
      [],
      [],
      ['city', 'state', 'country']
    ],
    badgeNameFormat: badgeConfiguration?.badgeNameFormat ?? 'firstName lastName',
    badgeLineFontSizes: badgeConfiguration?.badgeLineFontSizes ?? [22, 12, 12, 12, 12],
    badgeLabelFontSize: badgeConfiguration?.badgeLabelFontSize ?? 30,
    badgeContentAlignment: badgeConfiguration?.badgeContentAlignment ?? 'center',
    badgeOrientation: initialOrientation
  }

  const handleSubmit = async (values) => {
    setAddingConfiguration(true)
    if (onSavingChange) onSavingChange(true)
    const payload = { ...values }
    if (configurationId) {
      await updateBadgeConfiguration(
        configurationId,
        payload,
        conference?.id,
        () => {
          setAddingConfiguration(false)
          if (onSavingChange) onSavingChange(false)
          onClose()
        },
        () => {
          setAddingConfiguration(false)
          if (onSavingChange) onSavingChange(false)
        }
      )
    } else {
      await addBadgeConfiguration(
        { ...payload, createdAt: moment().utc().valueOf() },
        conference?.id,
        () => {
          setAddingConfiguration(false)
          if (onSavingChange) onSavingChange(false)
          onClose()
        },
        () => {
          setAddingConfiguration(false)
          if (onSavingChange) onSavingChange(false)
        }
      )
    }
  }

  return (
    <Box className={classes.container}>
      {!hideHeader && (
        <Stack direction="row" className={classes.header}>
          <Typography variant="h5" className={classes.pageTitle}>
            {title}
          </Typography>
          <IconButton
            className={classes.iconButton}
            onClick={requestClose}
            disabled={addingConfiguration}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}

      <ConfirmModal
        open={discardConfirmOpen}
        closeModal={() => setDiscardConfirmOpen(false)}
        dialogTitle="Unsaved changes"
        dialogText="You have unsaved changes to this badge configuration. If you leave now, they will be lost."
        handleConfirm={() => {
          setDiscardConfirmOpen(false)
          onClose()
        }}
      />

      <Wrapper inProgress={loadingConfiguration}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={addBadgeConfigurationSchema}
          onSubmit={handleSubmit}>
          {({ errors, values, dirty, setFieldValue, submitForm }) => {
            if (submitRef) submitRef.current = submitForm
            dirtyRef.current = dirty
            return (
              <>
                <Form className={classes.form}>
                  {hideHeader && (
                    <Box className={classes.pageHeader}>
                      <Typography variant="h6" className={classes.pageTitleText}>
                        {pageTitle}
                      </Typography>
                      <Box className={classes.pageHeaderRight}>
                        <Button
                          className={classes.backButton}
                          onClick={requestClose}
                          disabled={addingConfiguration}>
                          Back
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          className={classes.saveButton}
                          disabled={addingConfiguration}
                          startIcon={
                            addingConfiguration ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CheckCircleOutlineIcon />
                            )
                          }>
                          {addingConfiguration ? 'Saving...' : 'Save configuration'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  {/* Two-column layout: left = form, right = preview */}
                  <Grid container spacing={3}>
                    {/* ── LEFT COLUMN ── */}
                    <Grid item xs={12} md={7}>
                      <Paper className={classes.tabContainer}>
                        {/* Tab headers */}
                        <CustomTabs
                          tabs={['Setup', 'Badge Layout']}
                          activeTab={activeTab}
                          onChange={setActiveTab}
                        />

                        <Box className={classes.tabContent}>
                          {/* ── Tab 0: Setup ── */}
                          {activeTab === 0 && (
                            <Box>
                              {/* Section 1: Configuration identity */}
                              <Box className={classes.configRow}>
                                <Box className={classes.configTitleCol}>
                                  <Typography className={classes.fieldLabel}>
                                    CONFIGURATION TITLE
                                  </Typography>
                                  <CustomTextField
                                    name="title"
                                    placeholder="e.g. For Exhibitors, Default Print"
                                    type="text"
                                    size="small"
                                  />
                                </Box>
                                <Divider
                                  orientation="vertical"
                                  flexItem
                                  style={{ margin: '0 24px' }}
                                />
                                <Box className={classes.configAppliesToCol}>
                                  <Typography className={classes.fieldLabel}>
                                    TARGET ROLES
                                  </Typography>
                                  <Box className={classes.subjectGrid}>
                                    {badgesSubjectsApplication.map((subject) => {
                                      const isChecked = values.applicableSubjects.includes(subject)
                                      return (
                                        <Box
                                          key={subject}
                                          className={`${classes.subjectCard} ${
                                            isChecked ? classes.subjectCardChecked : ''
                                          }`}
                                          onClick={() => {
                                            const next = isChecked
                                              ? values.applicableSubjects.filter(
                                                  (s) => s !== subject
                                                )
                                              : [...values.applicableSubjects, subject]
                                            setFieldValue('applicableSubjects', next)
                                          }}>
                                          <Box
                                            className={`${classes.subjectCheckbox} ${
                                              isChecked ? classes.subjectCheckboxChecked : ''
                                            }`}>
                                            {isChecked && (
                                              <span className={classes.checkmark}>✓</span>
                                            )}
                                          </Box>
                                          <Typography className={classes.subjectName}>
                                            {subject}
                                          </Typography>
                                        </Box>
                                      )
                                    })}
                                  </Box>
                                  {errors.applicableSubjects && (
                                    <FormErrorMessage style={{ marginTop: 4 }}>
                                      {errors.applicableSubjects}
                                    </FormErrorMessage>
                                  )}
                                </Box>
                              </Box>

                              <Divider style={{ margin: '24px 0' }} />

                              {/* Section 2: Badge Appearance */}
                              <Typography className={classes.sectionTitle}>Badge Bar</Typography>
                              <Box className={classes.appearanceRow}>
                                <Box className={classes.setupColorField}>
                                  <Typography className={classes.fieldLabel}>
                                    BADGE BAR COLOR
                                  </Typography>
                                  <Box className={classes.colorRow} style={{ marginTop: 8 }}>
                                    {PRESET_COLORS.map((color) => (
                                      <Box
                                        key={color}
                                        className={classes.colorSwatch}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setFieldValue('badgeBarColor', color)}>
                                        {values.badgeBarColor === color && (
                                          <Box className={classes.swatchCheck}>✓</Box>
                                        )}
                                      </Box>
                                    ))}
                                    {!PRESET_COLORS.includes(values.badgeBarColor) && (
                                      <Box
                                        className={classes.colorSwatch}
                                        style={{ backgroundColor: values.badgeBarColor }}>
                                        <Box className={classes.swatchCheck}>✓</Box>
                                      </Box>
                                    )}
                                    <Box
                                      className={classes.colorPickerWrapper}
                                      ref={colorPickerRef}>
                                      <Box
                                        className={classes.addColorBtn}
                                        onClick={() => setColorPickerOpen((v) => !v)}>
                                        <AddIcon style={{ fontSize: 16 }} />
                                      </Box>
                                      {colorPickerOpen && (
                                        <Box className={classes.colorPickerPopover}>
                                          <HexColorPicker
                                            color={values.badgeBarColor}
                                            onChange={(color) =>
                                              setFieldValue('badgeBarColor', color)
                                            }
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                  {errors.badgeBarColor && (
                                    <FormErrorMessage>{errors.badgeBarColor}</FormErrorMessage>
                                  )}
                                </Box>
                                <Divider
                                  orientation="vertical"
                                  flexItem
                                  style={{ margin: '0 4px' }}
                                />
                                <Box className={classes.setupFontField}>
                                  <Typography className={classes.fieldLabel}>
                                    BADGE LABEL FONT
                                  </Typography>
                                  <Box
                                    className={classes.fontSizeInputWrapper}
                                    style={{ display: 'inline-flex', marginTop: 8 }}>
                                    <Box
                                      className={`${classes.fontSizeBtn} ${
                                        (values.badgeLabelFontSize ?? 30) <= 1
                                          ? classes.fontSizeBtnDisabled
                                          : ''
                                      }`}
                                      onClick={() => {
                                        const val = (values.badgeLabelFontSize ?? 30) - 1
                                        if (val >= 1) setFieldValue('badgeLabelFontSize', val)
                                      }}>
                                      −
                                    </Box>
                                    <input
                                      type="number"
                                      min={1}
                                      max={36}
                                      step={1}
                                      value={values.badgeLabelFontSize ?? 30}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value)
                                        if (isNaN(val)) return
                                        setFieldValue('badgeLabelFontSize', Math.min(36, Math.max(1, val)))
                                      }}
                                      className={classes.fontSizeInput}
                                    />
                                    <Box
                                      className={`${classes.fontSizeBtn} ${
                                        (values.badgeLabelFontSize ?? 30) >= 36
                                          ? classes.fontSizeBtnDisabled
                                          : ''
                                      }`}
                                      onClick={() => {
                                        const val = (values.badgeLabelFontSize ?? 30) + 1
                                        if (val <= 36) setFieldValue('badgeLabelFontSize', val)
                                      }}>
                                      +
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>

                              <Divider style={{ margin: '24px 0' }} />

                              {/* Section 3: QR Code Fields */}
                              <Box className={classes.qrFieldsHeader}>
                                <Box>
                                  <Typography className={classes.sectionTitle}>
                                    QR Code Fields
                                  </Typography>
                                  <Typography className={classes.sectionSubText}>
                                    Pick additional fields to include in the QR code (First Name and
                                    Last Name are always included)
                                  </Typography>
                                </Box>
                                {(() => {
                                  const selectableFields = badgeConfigurationDefaultFields.filter(
                                    (f) => f.key !== 'firstName' && f.key !== 'lastName'
                                  )
                                  const allSelected = selectableFields.every((f) =>
                                    values.fieldsAppliedInsideQRCode.includes(f.key)
                                  )
                                  return (
                                    <Box
                                      className={classes.qrBulkBtn}
                                      onClick={() =>
                                        setFieldValue(
                                          'fieldsAppliedInsideQRCode',
                                          allSelected
                                            ? ['firstName', 'lastName']
                                            : badgeConfigurationDefaultFields.map((f) => f.key)
                                        )
                                      }>
                                      {allSelected ? (
                                        <CheckBoxOutlineBlankIcon
                                          className={classes.qrBulkBtnIcon}
                                        />
                                      ) : (
                                        <CheckBoxOutlinedIcon className={classes.qrBulkBtnIcon} />
                                      )}
                                      {allSelected ? 'Unselect all' : 'Select all'}
                                    </Box>
                                  )
                                })()}
                              </Box>
                              <Box className={classes.qrFieldsGrid}>
                                {badgeConfigurationDefaultFields
                                  .filter((f) => f.key !== 'firstName' && f.key !== 'lastName')
                                  .map(({ key, value }) => {
                                    const isSelected =
                                      values.fieldsAppliedInsideQRCode.includes(key)
                                    return (
                                      <Box
                                        key={key}
                                        className={`${classes.qrFieldPill} ${
                                          isSelected ? classes.qrFieldPillActive : ''
                                        }`}
                                        onClick={() => {
                                          const next = isSelected
                                            ? values.fieldsAppliedInsideQRCode.filter(
                                                (f) => f !== key
                                              )
                                            : [...values.fieldsAppliedInsideQRCode, key]
                                          setFieldValue('fieldsAppliedInsideQRCode', next)
                                        }}>
                                        {isSelected && (
                                          <span className={classes.qrFieldCheckmark}>✓</span>
                                        )}
                                        <Typography className={classes.qrFieldLabel}>
                                          {value}
                                        </Typography>
                                      </Box>
                                    )
                                  })}
                              </Box>
                              {errors.fieldsAppliedInsideQRCode && (
                                <FormErrorMessage style={{ marginTop: 8 }}>
                                  {errors.fieldsAppliedInsideQRCode}
                                </FormErrorMessage>
                              )}
                            </Box>
                          )}

                          {/* ── Tab 1: Badge Layout ── */}
                          {activeTab === 1 && (
                            <BadgeLayoutSection values={values} setFieldValue={setFieldValue} />
                          )}
                        </Box>
                      </Paper>
                    </Grid>

                    {/* ── RIGHT COLUMN: Live Preview ── */}
                    <Grid item xs={12} md={5}>
                      <Box className={classes.previewPanel}>
                        <Box className={classes.previewHeader}>
                          <Box className={classes.liveDot} />
                          <Typography className={classes.livePreviewLabel}>LIVE PREVIEW</Typography>
                        </Box>

                        {/* Orientation + Content Alignment in one row */}
                        <Box className={classes.previewControlsTopRow}>
                          <Box className={classes.previewControlInline}>
                            <Typography className={classes.previewControlLabel}>
                              ORIENTATION
                            </Typography>
                            <Box className={classes.segmentedGroup}>
                              {['landscape', 'portrait'].map((orient) => (
                                <Box
                                  key={orient}
                                  className={`${classes.segmentedOption} ${
                                    (values.badgeOrientation || 'landscape') === orient
                                      ? classes.segmentedOptionActive
                                      : ''
                                  }`}
                                  onClick={() => {
                                    setFieldValue('badgeOrientation', orient)
                                    localStorage.setItem('badgeOrientation', orient)
                                  }}>
                                  {orient === 'landscape' ? (
                                    <CropLandscape style={{ fontSize: 13, marginRight: 4 }} />
                                  ) : (
                                    <CropPortrait style={{ fontSize: 13, marginRight: 4 }} />
                                  )}
                                  {orient.charAt(0).toUpperCase() + orient.slice(1)}
                                </Box>
                              ))}
                            </Box>
                            <Typography
                              style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                              {(values.badgeOrientation || 'landscape') === 'landscape'
                                ? '(Use Avery card stock - Template 5392)'
                                : '(Use PC/nametag 4" x 3" Holder-Less Zip Badge™ Template)'}
                            </Typography>
                          </Box>

                          <Box className={classes.previewControlInline}>
                            <Typography className={classes.previewControlLabel}>
                              CONTENT ALIGNMENT
                            </Typography>
                            <Box className={classes.segmentedGroup}>
                              {['center', 'left'].map((align) => (
                                <Box
                                  key={align}
                                  className={`${classes.segmentedOption} ${
                                    (values.badgeContentAlignment || 'center') === align
                                      ? classes.segmentedOptionActive
                                      : ''
                                  }`}
                                  onClick={() => setFieldValue('badgeContentAlignment', align)}>
                                  {align === 'center' ? 'Center' : 'Left'}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>

                        <Divider style={{ margin: '8px 0 12px' }} />

                        {/* Preview as + Badge card */}
                        {(() => {
                          const availableSubjects = badgesSubjectsApplication.filter((s) =>
                            values.applicableSubjects.includes(s)
                          )
                          const effectivePreviewAs = availableSubjects.includes(previewAs)
                            ? previewAs
                            : availableSubjects[0] || badgesSubjectsApplication[0]
                          return (
                            <>
                              {availableSubjects.length > 0 && (
                                <Box className={classes.previewControlRow}>
                                  <Typography className={classes.previewControlLabel}>
                                    PREVIEW AS
                                  </Typography>
                                  <Box className={classes.segmentedGroup}>
                                    {availableSubjects.map((subject) => (
                                      <Box
                                        key={subject}
                                        className={`${classes.segmentedOption} ${
                                          effectivePreviewAs === subject
                                            ? classes.segmentedOptionActive
                                            : ''
                                        }`}
                                        onClick={() => setPreviewAs(subject)}>
                                        {subject}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <BadgePreview
                                  badgeConfig={values}
                                  orientation={values.badgeOrientation || 'landscape'}
                                  previewAs={
                                    values.applicableSubjects.includes(previewAs)
                                      ? previewAs
                                      : values.applicableSubjects[0] || previewAs
                                  }
                                />
                              </Box>
                            </>
                          )
                        })()}
                      </Box>
                    </Grid>
                  </Grid>

                  <Box className={classes.bottomActions}>
                    <Button
                      size="large"
                      onClick={requestClose}
                      disabled={addingConfiguration}
                      className={classes.cancelButton}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      className={classes.saveButton}
                      disabled={addingConfiguration}
                      startIcon={
                        addingConfiguration ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleOutlineIcon />
                        )
                      }>
                      {addingConfiguration ? 'Saving...' : 'Save configuration'}
                    </Button>
                  </Box>
                </Form>
              </>
            )
          }}
        </Formik>
      </Wrapper>
    </Box>
  )
}

export default ConfigurationForm

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    padding: '0',
    boxSizing: 'border-box'
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0px',
    marginBottom: '24px',
    borderBottom: '1px solid #f0f0f0'
  },
  pageTitle: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#333'
  },
  pageHeader: {
    position: 'sticky',
    top: 1,
    zIndex: 99,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    padding: '12px 8px',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '16px'
  },
  pageTitleText: {
    fontWeight: 600,
    fontSize: '18px',
    color: '#333'
  },
  pageHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  backButton: {
    textTransform: 'none',
    color: '#555',
    fontSize: '14px'
  },
  saveButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px'
  },
  form: {
    padding: '0'
  },

  /* ── Tabs ── */
  tabContainer: {
    borderRadius: '8px !important',
    boxShadow: '0 1px 3px rgba(60,64,67,0.15), 0 1px 2px rgba(60,64,67,0.1) !important',
    backgroundColor: '#ffffff',
    overflow: 'visible'
  },
  tabContent: {
    padding: '20px 24px'
  },

  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    marginBottom: '4px',
    color: '#1a1a1a'
  },
  sectionSubText: {
    fontSize: '13px',
    color: '#888',
    marginBottom: '16px'
  },

  /* ── Setup tab layout ── */
  configRow: {
    display: 'flex',
    gap: '0',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  configTitleCol: {
    flex: '0 0 332px',
    minWidth: '180px'
  },
  configAppliesToCol: {
    flex: '1 1 auto',
    minWidth: '200px'
  },
  setupColorField: {
    flex: '0 0 332px',
    minWidth: '180px'
  },
  setupFontField: {
    flex: '0 0 auto'
  },

  fieldLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#aaa',
    letterSpacing: '0.5px',
    marginBottom: '4px',
    textTransform: 'uppercase'
  },
  fieldHint: {
    fontSize: '12px',
    color: '#b0b0b0',
    marginBottom: '4px',
    lineHeight: '1.4'
  },

  /* ── Badge Appearance row ── */
  appearanceRow: {
    display: 'flex',
    marginTop: '16px',
    gap: '24px',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start'
  },

  /* ── Color swatches ── */
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    paddingTop: '4px'
  },
  colorSwatch: {
    width: 34,
    height: 34,
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    flexShrink: 0
  },
  swatchCheck: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1
  },
  colorPickerWrapper: {
    position: 'relative'
  },
  addColorBtn: {
    width: 34,
    height: 34,
    borderRadius: '8px',
    border: '1.5px dashed #bbb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#888',
    '&:hover': {
      borderColor: '#999',
      color: '#555'
    }
  },
  colorPickerPopover: {
    position: 'absolute',
    top: '40px',
    left: 0,
    zIndex: 1300,
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    padding: '8px'
  },

  /* ── Applies to subject cards ── */
  subjectGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '4px'
  },
  subjectCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1.5px solid #e0e0e0',
    cursor: 'pointer',
    flex: '0 0 auto',
    backgroundColor: '#fff',
    transition: 'border-color 0.15s, background-color 0.15s',
    userSelect: 'none',
    boxSizing: 'border-box',
    '&:hover': {
      borderColor: '#b0d8e0'
    }
  },
  subjectCardChecked: {
    borderColor: '#00BCD4',
    backgroundColor: '#f0fbfd'
  },
  subjectCheckbox: {
    width: 16,
    height: 16,
    borderRadius: '3px',
    border: '2px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#fff'
  },
  subjectCheckboxChecked: {
    backgroundColor: '#00BCD4',
    borderColor: '#00BCD4'
  },
  checkmark: {
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: 1
  },
  subjectName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333'
  },

  fontSizeInputWrapper: {
    display: 'flex',
    alignItems: 'stretch',
    border: '1px solid #ddd',
    borderRadius: '5px',
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: '#00BCD4'
    }
  },
  fontSizeInput: {
    width: '36px',
    padding: '8px',
    fontSize: '12px',
    fontWeight: 500,
    border: 'none',
    outline: 'none',
    textAlign: 'center',
    background: '#fff',
    appearance: 'textfield',
    MozAppearance: 'textfield',
    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
      display: 'none'
    }
  },
  fontSizeBtn: {
    padding: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#555',
    backgroundColor: '#f5f5f5',
    borderRight: '1px solid #ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    lineHeight: 1,
    '&:last-child': {
      borderRight: 'none',
      borderLeft: '1px solid #ddd'
    },
    '&:hover': {
      backgroundColor: '#e8e8e8',
      color: '#222'
    }
  },
  fontSizeBtnDisabled: {
    cursor: 'default',
    color: '#ccc',
    pointerEvents: 'none',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      color: '#ccc'
    }
  },

  /* ── QR Code Fields pills ── */
  qrFieldsHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '4px'
  },
  qrBulkBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#666',
    cursor: 'pointer',
    userSelect: 'none',
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: '#00BCD4',
      color: '#00BCD4',
      backgroundColor: '#f0fbfd'
    }
  },
  qrBulkBtnIcon: {
    fontSize: '15px !important'
  },
  qrFieldsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '4px'
  },
  qrFieldPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '6px 14px',
    borderRadius: '20px',
    border: '1.5px solid #e0e0e0',
    backgroundColor: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: '#00BCD4',
      backgroundColor: '#f0fbfd'
    }
  },
  qrFieldPillActive: {
    borderColor: '#00BCD4',
    backgroundColor: '#f0fbfd'
  },
  qrFieldPillLocked: {
    cursor: 'not-allowed',
    opacity: 0.7,
    '&:hover': {
      borderColor: '#00BCD4',
      backgroundColor: '#f0fbfd'
    }
  },
  qrFieldCheckmark: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#00BCD4',
    lineHeight: 1
  },
  qrFieldLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333'
  },

  /* ── Live Preview panel ── */
  previewPanel: {
    position: 'sticky',
    top: 80,
    backgroundColor: '#fff',
    border: '1px solid #e8e8e8',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    maxHeight: 'calc(100vh - 120px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '14px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e8e8e8',
    flexShrink: 0
  },
  liveDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    flexShrink: 0,
    animation: '$blink 2s ease-in-out infinite'
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.25 }
  },
  livePreviewLabel: {
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    color: '#222'
  },
  previewControlsTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '8px',
    flexShrink: 0,
    flexWrap: 'wrap'
  },
  previewControlInline: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0
  },
  previewControlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
    flexWrap: 'wrap',
    flexShrink: 0
  },
  previewControlLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#aaa',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  },
  segmentedGroup: {
    display: 'inline-flex',
    border: '1.5px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#f5f5f5'
  },
  segmentedOption: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '11px',
    fontWeight: 500,
    color: '#888',
    cursor: 'pointer',
    padding: '5px 13px',
    backgroundColor: 'transparent',
    userSelect: 'none',
    transition: 'background-color 0.15s, color 0.15s',
    whiteSpace: 'nowrap',
    '&:not(:last-child)': {
      borderRight: '1.5px solid #e0e0e0'
    },
    '&:hover': { color: '#00BCD4', backgroundColor: '#eaf9fc' }
  },
  segmentedOptionActive: {
    backgroundColor: '#fff',
    color: '#00BCD4',
    fontWeight: 700,
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)'
  },
  /* ── Misc ── */
  bottomActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
    margin: '24px 0 8px',
    padding: '16px 0',
    borderTop: '1px solid #f0f0f0'
  },
  cancelButton: {
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#666',
    border: '1px solid #ccc',
    padding: '8px 20px',
    minWidth: '100px'
  },
  iconButton: {
    padding: '8px',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
  }
}))

const useLayoutStyles = makeStyles(() => ({
  layoutTopControls: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: '14px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f0f0f0'
  },
  linesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  linesGridRow: {
    display: 'flex',
    gap: '8px',
    '& > *': { flex: '1 1 0%', minWidth: 0 }
  },
  styleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  styleRowLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#666',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  },
  emptyLineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  alignmentBtnGroup: {
    display: 'flex',
    gap: '6px'
  },
  alignmentPill: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#444',
    cursor: 'pointer',
    padding: '3px 12px',
    borderRadius: '20px',
    border: '1.5px solid #e0e0e0',
    backgroundColor: '#fff',
    userSelect: 'none',
    transition: 'all 0.15s',
    '&:hover': { borderColor: '#00BCD4', color: '#00BCD4' }
  },
  alignmentPillActive: {
    borderColor: '#00BCD4',
    color: '#00BCD4',
    fontWeight: 700
  },
  availableBar: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px'
  },
  availableGuideText: {
    fontSize: '11px',
    color: '#888',
    fontStyle: 'italic',
    marginBottom: 2
  },
  availableBarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px'
  },
  availableTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  availableIconBox: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1.5px solid #00BCD4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  availableTitle: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#00BCD4',
    letterSpacing: '0.5px'
  },
  availableCount: {
    backgroundColor: '#00BCD4',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    borderRadius: '10px',
    padding: '1px 7px',
    lineHeight: '18px'
  },
  availableHint: {
    fontSize: '12px',
    color: '#888'
  },
  availableFieldsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  availableFieldChip: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 10px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'grab',
    gap: '4px',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: '#00BCD4',
      boxShadow: '0 1px 4px rgba(0,188,212,0.2)'
    }
  },
  availableFieldName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333'
  },
  availableFieldSample: {
    fontSize: '12px',
    color: '#aaa',
    marginLeft: '5px'
  },
  emptyAvailable: {
    fontSize: '13px',
    color: '#aaa',
    fontStyle: 'italic'
  },
  lineBlock: {
    border: '1px dashed #e0e0e0',
    borderRadius: '8px',
    padding: '10px 14px'
  },
  lineLabelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px'
  },
  lineLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#555',
    letterSpacing: '0.5px'
  },
  fontSizeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  fontSizeHeading: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#666',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
  },
  fontSizeInputWrapper: {
    display: 'flex',
    alignItems: 'stretch',
    border: '1px solid #ddd',
    borderRadius: '5px',
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: '#00BCD4'
    }
  },
  fontSizeInput: {
    width: '36px',
    padding: '7px 4px',
    fontSize: '12px',
    fontWeight: 500,
    border: 'none',
    outline: 'none',
    textAlign: 'center',
    background: '#fff',
    appearance: 'textfield',
    MozAppearance: 'textfield',
    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
      display: 'none'
    }
  },
  fontSizeBtn: {
    padding: '7px 7px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#555',
    backgroundColor: '#f5f5f5',
    borderRight: '1px solid #ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    lineHeight: 1,
    '&:last-child': {
      borderRight: 'none',
      borderLeft: '1px solid #ddd'
    },
    '&:hover': {
      backgroundColor: '#e8e8e8',
      color: '#222'
    }
  },
  fontSizeBtnDisabled: {
    cursor: 'default',
    color: '#ccc',
    pointerEvents: 'none',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      color: '#ccc'
    }
  },
  lockedText: {
    fontWeight: 400,
    fontStyle: 'italic',
    fontSize: '12px',
    color: '#888',
    letterSpacing: 0
  },
  lineDropZone: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    minHeight: '36px',
    alignItems: 'center',
    width: '100%',
    flex: 1
  },
  lockedChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '5px 12px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '6px'
  },
  lockedChipText: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#555'
  },
  lockedChipBadge: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#999',
    backgroundColor: '#e8e8e8',
    borderRadius: '4px',
    padding: '1px 6px',
    letterSpacing: '0.3px'
  },
  emptyLine: {
    fontSize: '13px',
    color: '#999',
    fontStyle: 'italic'
  },
  lineFieldChip: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 10px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    gap: '4px'
  },
  lineFieldName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333'
  },
  removeBtn: {
    fontSize: '16px',
    color: '#bbb',
    cursor: 'pointer',
    lineHeight: 1,
    marginLeft: '4px',
    padding: '0 2px',
    '&:hover': { color: '#e53935' }
  },
  nameFormatCard: {
    display: 'flex',
    padding: '8px 14px',
    border: '1.5px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s',
    backgroundColor: '#fff',
    minWidth: 110,
    '&:hover': { borderColor: '#00BCD4' }
  },
  nameFormatCardActive: {
    borderColor: '#00BCD4',
    backgroundColor: '#f0fbfd'
  },
  nameFormatRadio: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: '1.5px solid #bbb',
    flexShrink: 0,
    transition: 'all 0.15s'
  },
  nameFormatRadioActive: {
    border: '3.5px solid #00BCD4',
    backgroundColor: '#fff'
  },
  nameFormatCardLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#333'
  },
  nameFormatCardExample: {
    fontSize: '11px',
    color: '#888',
    fontStyle: 'italic',
    textAlign: 'center'
  }
}))
